import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import communityApi from '../../api/communityApi';
import { AssetInterface } from "../../store/community/assetsSlice";
import assetMakerUrls from "../../configs/community/communityUrls";
import { addDays, addYears } from "../../helpers/DateHelpers";

const PREFIX = 'community';

const processStrategy = (value: any) => {
  return {
    ...value,
    url: assetMakerUrls.assetPath(value.id),
    thumbnail_url: assetMakerUrls.remoteUrl(value.thumbnail_url),
    file_url: assetMakerUrls.remoteUrl(value.file_url),
    published: new Date(value.published_at) < new Date()
  };
}

export const assetsEntity = new schema.Entity(
  'assets',
  {},
  {
    processStrategy
  }
);

export const assetEntity = new schema.Entity(
  'asset',
  {},
  {
    processStrategy
  }
);

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      asset: AssetInterface
    }
  >(response.data.asset, assetEntity);
  return normalized.entities;
}

const normalizeUploadInstance = (response: any) => {
  const normalized = normalize<
    any,
    {
      asset: AssetInterface
    }
    >(response.asset, assetEntity);
  return normalized.entities;
}

const normalizeCollection = (response: AxiosResponse, silentId?: number) => {
  const normalized  = normalize<
    any,
    {
      assets: { [key: string]: AssetInterface },
    }
    >(response.data.assets, [assetsEntity]);

  return {
    assets: normalized.entities?.assets || {},
    ids: normalized.result,
    count: response.data.meta?.total_count,
    silentId: silentId
  };
}

export const fetchAssets = createAsyncThunk(
  `${PREFIX}_assets/fetchAssets`,
  async ({ params, silentId }: { params: object; silentId?: number }) => {
    const response = await communityApi.get(`/onesky/assets`, { params })
    return normalizeCollection(response, silentId);
  }
);

export const uploadAsset = createAsyncThunk(
  `${PREFIX}_assets/createAsset`,
  async (response: object, { rejectWithValue }) => {
    try {
      return normalizeUploadInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const createAsset = createAsyncThunk(
  `${PREFIX}_assets/createAsset`,
  async (data: object, { rejectWithValue }) => {
    try {
      const response = await communityApi.post(`/onesky/assets`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const fetchAsset = createAsyncThunk(
  `${PREFIX}_assets/fetchAsset`,
  async (id: number | string) => {
    const response = await communityApi.get(`/onesky/assets/${id}`);
    return normalizeInstance(response);
  }
);

export const updateAsset = createAsyncThunk(
  `${PREFIX}_assets/updateAsset`,
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await communityApi.patch(`/onesky/assets/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyAsset = createAsyncThunk(
  `${PREFIX}_assets/destroyAsset`,
  async (id: number) => {
    await communityApi.delete(`/onesky/assets/${id}`);
    return { id: id };
  }
);

export const toggleShow = createAsyncThunk(
  `${PREFIX}_assets/updateAsset`,
  async ({ id, published }: { id: number, published: boolean }, { rejectWithValue }) => {
    try {
      const published_at = published ? addYears(10) : addDays(0);

      const response = await communityApi.patch(`/onesky/assets/${id}`, { published_at: published_at });
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);
